import 'twin.macro';
import React from 'react';
import { ElevesView } from '@teetsh/app/src/types/eleves';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import { EleveListData } from '@teetsh/app/src/containers/Eleves/List/Table/EleveList';
import { Views } from '@teetsh/app/src/containers/Eleves/List/Table/Views';
import {
  Outlet,
  useLocation,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import SchoolyearRedirect from '@teetsh/app/src/components/Redirect/SchoolyearRedirect';
import useElevesViews from '@teetsh/app/src/queries/eleves/views/useElevesViews';
import { elevesUrls } from '@teetsh/app/src/containers/Eleves/urls';
import useElevesCustomFields from 'queries/eleves/customFields/useElevesCustomFields';
import ScreenSpinner from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { useTranslation } from 'react-i18next';
import ButtonExternal from '@teetsh/app/src/components/Buttons/ExternalLink';
import Tooltip2 from '@teetsh/app/src/components/Tooltip2';
import { tutoUrls } from 'containers/tutoUrls';
import { QuestionMarkCircleIcon as QuestionMarkCircle } from '@heroicons/react/24/outline';

export function RedirectToAnotherView() {
  const location = useLocation();
  const { data, isLoading, isFetched } = useElevesViews();
  if (isLoading || !isFetched || !data) {
    return <ScreenSpinner />;
  }
  return (
    <SchoolyearRedirect
      to={`${elevesUrls.viewDetails(data[0].id)}/${location.search}`}
    />
  );
}

export function ViewRoot() {
  const { views } = useOutletContext<{
    views: ElevesView[];
    isReady: boolean;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
  }>();
  const { data: customFields, isLoading } = useElevesCustomFields();

  const classrooms = useClassrooms();
  const { viewId } = useParams<{ viewId: string }>();
  const matchingView = views.find(v => v.id === viewId);

  if (isLoading && !customFields) {
    return <ScreenSpinner />;
  }
  if (!matchingView || !viewId) {
    return <RedirectToAnotherView />;
  }
  return (
    <EleveListData
      classrooms={classrooms}
      customFields={customFields || []}
      view={matchingView}
    />
  );
}

export function ViewsTable() {
  const { t } = useTranslation(['eleve']);
  const context = useOutletContext<{
    views: ElevesView[];
    isReady: boolean;
  }>();
  if (!context.isReady) {
    return (
      <>
        <div className="tsh-skeleton" tw="mb-3 h-20" />
        <div className="tsh-skeleton" tw="h-20" />
      </>
    );
  }

  return (
    <div tw="md:pt-6 pb-8 px-2 md:px-6">
      <div tw="px-2">
        <div>
          <div tw="flex items-center justify-between gap-2 sm:mb-2 sm:border-b border-neutral-quiet">
            <Views views={context.views} />
            <Tooltip2 label={t('eleve:openVideoTutorial')}>
              <ButtonExternal
                target="_blank"
                href={tutoUrls.eleveVideoTutorialUrl}
                small
                minimal
                icon={QuestionMarkCircle}
                tw="px-2"
              />
            </Tooltip2>
          </div>
          <Outlet context={context} />
        </div>
      </div>
    </div>
  );
}
